import { Fragment, type MouseEvent } from 'react';
import { idDeLaReferencia, referenciasDe, validarEstructura, VERSION_SPEC, type Bloque } from '@athena/shared';
import { diccionarioDe, type Idioma } from '../../../i18n/index.js';
import { GraficoDeHechos } from './GraficoDeHechos.js';
import { ListaEstudios } from './ListaEstudios.js';
import { ListaFuentes } from './ListaFuentes.js';
import { Sugerencias } from './Sugerencias.js';
import { TarjetasLadoALado } from './TarjetasLadoALado.js';
import { TextoConMarcas } from './TextoConMarcas.js';
import { TextoEnriquecido } from './TextoEnriquecido.js';
import type { RespuestaDeAthena } from '../../../common/services/tipos.js';
import { anotarEvento } from '../../../common/services/api.js';

/**
 * Pinta la respuesta: recorre la lista plana de bloques y da a cada uno su componente.
 *
 * **Vuelve a validar antes de pintar**, aunque el API ya validó. No es desconfianza del propio
 * código: entre las dos validaciones hay una base de datos y una red, y una respuesta guardada hace
 * meses puede venir de una versión de la spec que ya no existe. Si no valida, se enseña la lista de
 * fuentes y una nota, que es más honrado que pintar algo a medias.
 */
export function Renderer({ respuesta, idioma }: { readonly respuesta: RespuestaDeAthena; readonly idioma: Idioma }) {
  const t = diccionarioDe(idioma).consulta;

  const disponibles = new Set(respuesta.fuentes.map(f => f.hechoId));
  const validacion = validarEstructura({ version: VERSION_SPEC, bloques: respuesta.bloques }, { hechosDisponibles: disponibles, delModelo: false });

  if (!validacion.valida) {
    return (
      <section aria-live='polite' data-estado='invalida'>
        <p className='font-(family-name:--text) text-[15px] text-(--ink-soft)'>{t.noSeHaComprobado}</p>
        <ListaFuentes respuesta={respuesta} idioma={idioma} />
      </section>
    );
  }

  function abrirReferencia(evento: MouseEvent<HTMLElement>) {
    const enlace = (evento.target as Element).closest('a');
    const destino = enlace?.getAttribute('href');
    if (!destino?.startsWith('#f')) return;
    if (evento.ctrlKey || evento.metaKey || evento.shiftKey || evento.altKey) return;
    const referencia = document.getElementById(destino.slice(1));
    if (referencia === null || !evento.currentTarget.contains(referencia)) return;
    evento.preventDefault();
    const desplegable = referencia.closest('details');
    if (desplegable !== null) desplegable.open = true;
    referencia.focus({ preventScroll: true });
    referencia.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  return (
    <section
      aria-live='polite'
      data-estado={respuesta.estado}
      onClick={abrirReferencia}
      className={`grid min-w-0 gap-8 ${respuesta.fuentes.length > 0 && respuesta.bloques.some(b => b.tipo === 'ListaFuentes') ? 'min-[1100px]:grid-cols-[minmax(0,1fr)_340px] min-[1100px]:gap-12' : ''}`}
    >
      <div className='min-w-0'>
        {/* Sin «Hemos entendido» (Iván, 2026-09-27): es funcionamiento interno y al usuario le importa poco. Los supuestos siguen en la traza. */}
        {respuesta.bloques
          .filter(b => b.tipo !== 'ListaFuentes')
          .map((bloque, i) => (
            <Fragment key={claveDe(bloque, i)}>{pintar(bloque, respuesta, idioma)}</Fragment>
          ))}
      </div>
      {respuesta.bloques.some(b => b.tipo === 'ListaFuentes') ? <ListaFuentes respuesta={respuesta} idioma={idioma} /> : null}
    </section>
  );
}

/**
 * La clave de React sale del **contenido** del bloque y no solo de su posición: en la segunda
 * vuelta del juez la respuesta se rehace entera, y con la posición como clave React reutilizaría el
 * nodo anterior y dejaría texto viejo en pantalla.
 */
function claveDe(bloque: Bloque, indice: number): string {
  const contenido = 'texto' in bloque ? bloque.texto.slice(0, 40) : referenciasDe(bloque).map(idDeLaReferencia).join('-').slice(0, 80);
  return `${String(indice)}-${bloque.tipo}-${contenido}`;
}

function pintar(bloque: Bloque, respuesta: RespuestaDeAthena, idioma: Idioma) {
  const t = diccionarioDe(idioma).consulta;

  switch (bloque.tipo) {
    case 'Parrafo':
      return (
        // Con su Markdown acotado (subtítulos, listas, citas destacadas); sin él, un <p> como siempre.
        <TextoEnriquecido texto={bloque.texto} respuesta={respuesta} idioma={idioma} />
      );

    case 'NoMedido':
      return (
        <div className='mb-[18px] rounded-lg border border-(--line) bg-(--chat-bg) p-[18px]'>
          <p className='m-0 font-(family-name:--text) text-[16px] leading-[1.6] text-(--ink)'>
            <TextoConMarcas texto={bloque.texto} respuesta={respuesta} idioma={idioma} />
          </p>
        </div>
      );

    case 'FraseCopiable':
      return <FraseCopiable texto={bloque.texto} citas={bloque.citas} respuesta={respuesta} idioma={idioma} />;

    case 'Veredicto':
      return (
        <div className='mb-[18px] rounded-lg border border-(--line-strong) p-[18px]' data-categoria={bloque.categoria}>
          {/* La categoría va con texto y no solo con color: es un requisito de accesibilidad y, además, un color no dice qué falla. */}
          <p className='m-0 mb-2 font-(family-name:--text) text-[13px] font-semibold tracking-[0.04em] text-(--teal-deep) uppercase'>
            {t.veredicto[bloque.categoria]}
          </p>
          <p className='m-0 font-(family-name:--text) text-[16px] leading-[1.6] text-(--ink)'>
            <TextoConMarcas texto={bloque.texto} respuesta={respuesta} idioma={idioma} />
          </p>
        </div>
      );

    case 'LadoALado':
      return <TarjetasLadoALado tarjetas={bloque.tarjetas} respuesta={respuesta} idioma={idioma} />;

    case 'Grafico':
      return <GraficoDeHechos grafico={bloque} respuesta={respuesta} idioma={idioma} />;

    case 'Nota':
      return (
        <p className='mb-[18px] font-(family-name:--text) text-[14px] text-(--ink-soft)' data-clase={bloque.clase}>
          {bloque.texto}
        </p>
      );

    case 'ListaFuentes':
      return <ListaFuentes respuesta={respuesta} idioma={idioma} />;

    case 'Sugerencias':
      return <Sugerencias sugerencias={respuesta.sugerencias} idioma={idioma} />;

    case 'ListaEstudios':
      return <ListaEstudios lista={bloque} idioma={idioma} />;
  }
}

function FraseCopiable({
  texto,
  citas,
  respuesta,
  idioma,
}: {
  readonly texto: string;
  readonly citas: readonly string[];
  readonly respuesta: RespuestaDeAthena;
  readonly idioma: Idioma;
}) {
  const t = diccionarioDe(idioma).consulta;
  const fuentes = citas.map(idDeLaReferencia).map(id => respuesta.fuentes.find(f => f.hechoId === id));

  /** Lo que se copia lleva la cita pegada: una cifra sin su fuente fuera de Athena no vale nada. */
  const paraElPortapapeles = [
    texto,
    ...fuentes.map(f =>
      f === undefined
        ? ''
        : `${t.grafico.fuente}: ${f.editor}${f.anio === null ? '' : `, ${String(f.anio)}`}${f.pagina === null ? '' : `, p. ${String(f.pagina)}`}`,
    ),
  ]
    .filter(l => l !== '')
    .join('\n');

  return (
    <blockquote className='mb-[18px] rounded-lg border-l-[3px] border-(--teal-deep) bg-(--chat-bg) py-[14px] pr-[18px] pl-[18px]'>
      <p className='m-0 font-(family-name:--text) text-[16px] leading-[1.6] text-(--ink)'>{texto}</p>
      <button
        type='button'
        className='mt-[10px] cursor-pointer border-none bg-transparent p-0 font-(family-name:--text) text-[13px] text-(--teal-deep) underline'
        onClick={() => {
          void navigator.clipboard.writeText(paraElPortapapeles);
          anotarEvento('copia', respuesta.preguntaId);
        }}
      >
        {t.copiar}
      </button>
    </blockquote>
  );
}
